import { CoreConfig } from '@fanduel/cpe-um-bundle/core-config/library';
import { CoreDeeplinks } from '@fanduel/cpe-um-bundle/core-deeplinks/library';
import { Account } from '@fanduel/cpe-um-bundle/account/library';
import { getUMConfig } from '@/utils/unified-modules/unified-modules';
import { ModalPresenter } from '@fanduel/cpe-um-bundle/core-modal-presenter/library';
import { CoreApiIdentities } from '@fanduel/cpe-um-bundle/core-api-identities/library';
import { CoreWebViewPluginRegistry } from '@fanduel/cpe-um-bundle/core-webview/library';
import { Logger } from './utils/logger';
import { Wallet } from '@fanduel/cpe-um-bundle/wallet/library';
import { AccountHub } from '@fanduel/cpe-um-bundle/account-hub/library';
import { CoreEvents } from '@fanduel/cpe-um-bundle/core-events/library';
import type { Event } from '@fanduel/cpe-um-bundle/core-events/contract';
import { getConfig } from '@/utils/config/config';
import { ResponsibleGaming } from '@fanduel/cpe-um-bundle/responsible-gaming/library';
import { SnowplowUm } from '@fanduel/snowplow-um';
import type { SnowplowConfig } from '@/tracking/snowplow/snowplow.types';
import { isAndroidWrapper } from '@/logic/platform';
import { CorePx } from '@fanduel/cpe-um-bundle/core-px/library';
import { TransactionHistoryLinkBlockPlugin } from '@/app/components/transaction-history/transaction-history-link-block-plugin';
import { ddRumError } from '@/utils/datadog';

let initialized: boolean = false;
let initializingPromise: Promise<boolean> | null = null;

const logger = new Logger();
const umLogger = new Logger(' ', '#fff', false);

type ConfigProps = {
    services: {
        snowplow: SnowplowConfig;
    };
};

const initializeSnowplow = async (config: ConfigProps) => {
    try {
        SnowplowUm.initialize();
    } catch (error) {
        console.error('Error initializing Snowplow UM:', error);
        return;
    }

    const snowplowConfig: SnowplowConfig = config?.services?.snowplow;

    if (snowplowConfig) {
        const appId = isAndroidWrapper() ? snowplowConfig.androidAppId : snowplowConfig.appId;
        try {
            SnowplowUm.postInitialize({ ...snowplowConfig, appId });
        } catch (error) {
            console.error('Error initializing Snowplow UM:', error);
        }
    }
};

const handleCoreEvent = (event: Event) => {
    const prefix = `[${event.vendorTag}] [${event.name}]`;

    if (!event.data) {
        umLogger.debug(prefix);
        return;
    }

    const method = umLogger[event.logLevel as keyof Logger] ?? umLogger.debug;
    try {
        const json = JSON.parse(event.data);

        method(`${prefix} ${json.log.message}`);
    } catch {
        method(`${prefix} ${event.data}`);
    }
};

const initModules = async () => {
    if (initialized) {
        logger.warn('Unified modules already initialized.');
        return;
    }

    CoreWebViewPluginRegistry.add({
        id: 'show-header',
        onUrlLoaded: (ref) => {
            ref.updateConfig((conf) => ({
                ...conf,
                showHeader: true,
                showFooter: true
            }));
        }
    });

    CoreWebViewPluginRegistry.add(TransactionHistoryLinkBlockPlugin);

    const conf = await getUMConfig();

    if (!conf?.appDomain?.region) {
        ddRumError({
            name: 'MISSING_REGION',
            description: 'Missing appDomain region when initalizing UMs',
            use_case: 'GLOBAL'
        });
        location.reload(); // Enforce reload to make sure region cookie is set through middleware
        return;
    }

    await CoreConfig.setConfig(conf);
    await CoreEvents.initialize();

    const config = getConfig();
    if (config.log.logUnifiedModulesEvents) {
        CoreEvents.subscribe(handleCoreEvent);
    }

    CoreApiIdentities.initialize();
    ModalPresenter.initialize();

    await CorePx.initialize();
    await CoreDeeplinks.initialize();
    await Account.initialize();
    await Wallet.initialize();
    await AccountHub.initialize();
    await ResponsibleGaming.initialize();
    await initializeSnowplow(config);

    initialized = true;
};

export const refreshConfig = async () => {
    const conf = await getUMConfig();
    await CoreConfig.setConfig(conf);
};

export const initializeUnifiedModules = async () => {
    if (initialized) {
        return initialized;
    }

    if (initializingPromise) {
        await initializingPromise;
        return initialized;
    }

    initializingPromise = new Promise((resolve, reject) => {
        logger.log('Started initalizing unified modules...');
        initModules()
            .then(() => {
                logger.log('Unified modules initialized successfully.');
                resolve(initialized);
            })
            .catch((error) => {
                logger.error('Error initializing unified modules:', error);
                reject(error);
            })
            .finally(() => {
                initializingPromise = null; // Reset the promise after initialization
            });
    });

    return initializingPromise;
};
